//@ts-check
import { createAction } from 'redux-actions';
import { createSelector } from 'reselect';

/** @type {(payload: { target: string, data: any }) => import('redux-actions').Action<any>} */
export const initEntity = createAction('@@entity/INIT_ENTITY');
/** @type {(payload: { target: string, diff: any, id?: string }) => import('redux-actions').Action<any>} */
export const updateEntity = createAction('@@entity/UPDATE_ENTITY');
/** @type {(payload: { target: string, data: any, id: string }) => import('redux-actions').Action<any>} */
export const createEntity = createAction('@@entity/CREATE_ENTITY');
/** @type {(payload: { target: string, id: string }) => import('redux-actions').Action<any>} */
export const deleteEntity = createAction('@@entity/DELETE_ENTITY');
/** @type {(payload: { target: string, data: any, id: string }) => import('redux-actions').Action<any>} */
export const replaceEntity = createAction('@@entity/REPLACE_ENTITY');
/**
 * @template T
 * @typedef {[
  * (state: any, id: string) => T,
  * (state: any) => { [x: string]: T },
  * (state: any) => string[],
  * (state: any) => T[]
  * ]} entitySelectors
  */

/**
 * @template T
 * @param {string[]} targets 
 * @returns {entitySelectors<T>}
 */
export const createEntitySelector = (targets) => {
  const getTarget = (state) => targets.reduce((obj, target) => obj[target], state);
  const get = (state, id) => getTarget(state).byId[id];
  const getById = state => getTarget(state).byId;
  const getAllIds = state => getTarget(state).allIds;
  const getAll = createSelector(getById, getAllIds, (byId, allIds) => allIds.map(id => byId[id]));
  return [
    get,
    getById,
    getAllIds,
    getAll
  ];
};

export const normalizedState = { byId: {}, allIds: [] };

const createEntityRaw = (id, data, state) => {
  return {
    ...state,
    byId: {
      ...state.byId,
      [id]: data
    },
    allIds: state.allIds.includes(id) ? state.allIds : [...state.allIds, id]
  };
};

const createEntitiesRaw = (idDataTupleArray, state) => {
  let newState = { byId: { ...state.byId }, allIds: [...state.allIds] };
  for (const [id, data] of idDataTupleArray) {
    newState.byId[id] = data;
    if (!newState.allIds.includes(id)) {
      newState.allIds.push(id);
    }
  }
  return newState;
};

const updateEntityRaw = (id, diff, state) => {
  return {
    ...state,
    byId: {
      ...state.byId,
      [id]: {
        ...state.byId[id],
        ...diff
      }
    }
  };
};

const deleteEntityRaw = (id, state) => {
  // eslint-disable-next-line no-unused-vars
  const { [id]: _deleted, ...byId } = state.byId;
  return {
    ...state,
    byId,
    allIds: state.allIds.filter(_id => id != _id)
  };
};

const deleteEntitiesRaw = (ids, state) => {
  let newState = { byId: { ...state.byId }, allIds: [...state.allIds] };
  let idSet = new Set(ids);
  for (const id of ids) {
    delete newState.byId[id];
  }
  newState.allIds = newState.allIds.filter(id => !idSet.has(id));
  return newState;
};

export default (state, action) => {
  const payload = action.payload;
  switch (action.type) {
    case String(initEntity):
      if (state[payload.target] == null) {
        return state;
      }
      return {
        ...state,
        [payload.target]: payload.data
      };

    case String(createEntity): {
      const { id, target, data } = payload;
      const entity = state[target];
      if (entity == null) {
        return state;
      }

      return {
        ...state,
        [target]: createEntityRaw(id, data, entity)
      };
    }

    case String(replaceEntity): {
      const { id, target, data } = payload;
      const entity = state[target];
      if (entity == null) {
        return state;
      }

      if (entity.byId[id] == null) {
        return {
          ...state,
          [target]: createEntityRaw(id, data, entity)
        };
      }

      return {
        ...state,
        [target]: {
          ...entity,
          byId: {
            ...entity.byId,
            [id]: data
          }
        }
      };
    }

    case String(updateEntity): {
      const { id, target, diff } = payload;
      const entity = state[target];
      if (entity == null) {
        return state;
      }

      if (!('byId' in entity)) {
        return {
          ...state,
          [target]: { ...entity, ...diff }
        };
      }

      if (entity.byId[id] == null) {
        return state;
      }

      return {
        ...state,
        [target]: updateEntityRaw(id, diff, entity)
      };
    }

    case String(deleteEntity): {
      const { id, target } = payload;
      const entity = state[target];
      if (entity == null) {
        return state;
      }

      return {
        ...state,
        [target]: deleteEntityRaw(id, entity)
      };

    }

    default:
      return state;
  }
};

export const entityHelper = Object.freeze({
  create: createEntityRaw,
  update: updateEntityRaw,
  delete: deleteEntityRaw,
  createMany: createEntitiesRaw,
  deleteMany: deleteEntitiesRaw,
});